/**
 * @fileoverview Hook Data Layer para gestión de direcciones de cliente en Inbox.
 * @module useCustomerAddressesData
 * @category Hook - Data Layer
 * @since 3.2.0
 * @version 3.2.0
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import customersModelMc from "../../../models/CustomersModelMc";
import { LOAD_DATA_FAILURE } from "../../../../actions/inbox/conversationActions";

/**
 * Hook Data Layer para cargar y gestionar direcciones de un cliente.
 *
 * Implementa el patrón de **Data Layer** con separación estricta de responsabilidades:
 * - Carga inicial automática de direcciones al montar/cambiar customer
 * - Exposición de loading, addresses y función de recarga
 * - NO maneja lógica de UI ni acciones de negocio
 *
 * @function useCustomerAddressesData
 * @param {Object} customer - Objeto customer con web_id o webId
 * @returns {Object} { addresses, loading, loadAddresses, removeAddress }
 * @see CustomerAddressesMC Para integración en componente
 */
export default function useCustomerAddressesData(customer) {
  const { t } = useTranslation('InboxMC');
  const dispatch = useDispatch();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * Carga las direcciones del cliente desde la API.
   * @memberof useCustomerAddressesData
   */
  const loadAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await customersModelMc.getCustomerAddresses(customer.web_id);
      setAddresses(data.addresses || []);
    } catch (error) {
      dispatch({
        type: LOAD_DATA_FAILURE,
        error: error.message || 'Error loading addresses.',
      });
    } finally {
      setLoading(false);
    }
  }, [customer.web_id, dispatch]);

  /**
   * Elimina una dirección del cliente (placeholder, implementar lógica real si aplica).
   * @memberof useCustomerAddressesData
   */
  const removeAddress = useCallback(async (address) => {
    const ok = window.confirm(
      t('location.delete_address_confirmation')
    );
    if (!ok) return;
    try {
      await customersModelMc.removeCustomerAddresses(customer.web_id, { id: address.id });
      setAddresses((prev) => prev.filter(a => a.id !== address.id));
    } catch (error) {
      const errorMessage = error?.message || error || 'Error deleting address.';
      dispatch({ type: LOAD_DATA_FAILURE, error: errorMessage });
    }
  }, [customer.web_id, dispatch, t]);

  // Carga inicial de direcciones al montar/cambiar customer
  useEffect(() => {
    if (customer?.web_id) {
      loadAddresses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.web_id]);

  return { addresses, loading, loadAddresses, removeAddress };
}
